/**
 * Where the horse is, and whether you were on it — across a view rebuild.
 *
 * The server has no map, so it stores only WHICH horse you tamed; the animal's
 * position and the mounted flag have always been client state living inside the
 * `Riding` object. And `buildScene()` destroys that object and re-creates it —
 * which happens every time the farm view is rebuilt, i.e. every time you switch
 * to the Shop tab and back. So the horse was re-placed at the fixed spot east of
 * the plots and the hero was back on foot: owner, "when I'm switching tabs from
 * farm to shop, and I was on horse, the horse disappears back to tent."
 *
 * The hero's own position already survives that (map/last-position.ts) and this
 * is the same idea for his horse, with the same guards:
 *
 *  - keyed by ACCOUNT, so one player's horse is not another's (the tutorial-flag
 *    bug taught this one);
 *  - keyed by SEED too, because the position is a tile on a specific map;
 *  - and it expires, so a horse left somewhere a month ago does not reappear in
 *    the middle of a world the player has since reshaped.
 *
 * Storage failures are never fatal: a horse that cannot be remembered simply
 * starts where it always did.
 */

const KEY = 'tb-horse-state';

/** How long a remembered position is trusted. Matches the hero's own memory. */
export const HORSE_MEMORY_MS = 12 * 60 * 60 * 1000;

export interface HorseState {
  seed: string;
  tx: number;
  ty: number;
  /** True if the hero was in the saddle when the view went away. */
  mounted: boolean;
  at: number;
}

export function saveHorseState(seed: string, tx: number, ty: number, mounted: boolean): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ seed, tx, ty, mounted, at: Date.now() } satisfies HorseState));
  } catch { /* private mode */ }
}

/** The remembered horse for this seed, or null if there is none worth trusting. */
export function loadHorseState(seed: string, now = Date.now()): HorseState | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<HorseState>;
    if (!s || s.seed !== seed) return null;                       // another account, or another map
    if (typeof s.tx !== 'number' || typeof s.ty !== 'number') return null;
    if (!Number.isFinite(s.tx) || !Number.isFinite(s.ty)) return null;
    if (typeof s.at !== 'number' || now - s.at > HORSE_MEMORY_MS) return null;
    return { seed, tx: s.tx, ty: s.ty, mounted: !!s.mounted, at: s.at };
  } catch { return null; }
}

/** Releasing or losing the horse must also forget where it stood. */
export function clearHorseState(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}
